import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { t } from "../../locales";
import { PUT } from "../../services/axios.service";
import { useToastHandler } from "../../hooks/useToastHandler";

type FormData = {
  old_password: string;
  new_password: string;
  confirm_password: string;
};

const PasswordChangeForm = () => {
  const { handleSuccess, handleError } = useToastHandler();
  const [isLoading, setIsLoading] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    handleSubmit,
    register,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormData>();

  const newPassword = watch("new_password");

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    try {
      const response: any = await PUT("/v1/change-password", {
        old_password: data.old_password,
        new_password: data.new_password,
        confirm_password: data.confirm_password,
      });
      if (response.data.status === "success") {
        handleSuccess(response);
        reset();
      } else {
        handleError(response);
      }
    } catch (error: any) {
      handleError(error);
    }
    setIsLoading(false);
  };

  const EyeIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );

  const EyeOffIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );

  const inputBase =
    "w-full h-12 pl-12 pr-12 text-sm text-brand-dark bg-white border-2 rounded-xl outline-none box-border transition-[border-color,box-shadow] duration-200 placeholder:text-gray-400 focus:border-brand-dark focus:shadow-focus-brand read-only:bg-gray-100 read-only:text-gray-500 read-only:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed";

  const renderPasswordField = (
    fieldName: "old_password" | "new_password" | "confirm_password",
    label: string,
    placeholder: string,
    showPassword: boolean,
    setShowPassword: (v: boolean) => void,
    validation: Record<string, any>,
    errorMessages: Record<string, string>
  ) => (
    <div className="flex flex-col">
      <label className="text-sm font-semibold text-brand-dark mb-[7px]">
        {label}<span className="text-red-500 ml-1">*</span>
      </label>
      <div className="relative">
        <span className="absolute left-[14px] top-1/2 -translate-y-1/2 text-brand-light pointer-events-none">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </span>
        <input
          className={`${inputBase} ${errors[fieldName] ? "border-red-400 shadow-focus-error" : "border-gray-200"}`}
          type={showPassword ? "text" : "password"}
          placeholder={placeholder}
          {...register(fieldName, validation)}
        />
        <button
          type="button"
          className="absolute right-3 top-1/2 -translate-y-1/2 bg-transparent border-none cursor-pointer p-1 flex items-center text-brand-light transition-colors duration-150 hover:text-brand-dark focus:outline-none"
          onClick={() => setShowPassword(!showPassword)}
        >
          {showPassword ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      </div>
      {errors[fieldName] && (
        <span className="text-sm text-red-500 mt-1 flex items-center gap-1">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          {errorMessages[errors[fieldName]?.type as string] || ""}
        </span>
      )}
    </div>
  );

  return (
    <div className="bg-white rounded-2xl shadow-card overflow-hidden">
      {/* Gray header band */}
      <div className="bg-gray-50 px-8 py-5 border-b border-gray-100">
        <h3 className="text-xl font-bold text-brand-dark">{t.profile.changePasswordTitle}</h3>
        <p className="text-sm text-gray-500 mt-1">{t.profile.changePasswordSubtitle}</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="p-8">
        {/* 3-column grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
          {renderPasswordField(
            "old_password",
            t.profile.oldPasswordLabel,
            t.profile.oldPasswordPlaceholder,
            showOldPassword,
            setShowOldPassword,
            { required: "required" },
            { required: t.profile.oldPasswordRequired }
          )}

          {renderPasswordField(
            "new_password",
            t.profile.newPasswordLabel,
            t.profile.newPasswordPlaceholder,
            showNewPassword,
            setShowNewPassword,
            { required: "required", minLength: 8 },
            { required: t.profile.newPasswordRequired, minLength: t.profile.newPasswordMin }
          )}

          {renderPasswordField(
            "confirm_password",
            t.profile.confirmPasswordLabel,
            t.profile.confirmPasswordPlaceholder,
            showConfirmPassword,
            setShowConfirmPassword,
            {
              required: "required",
              validate: (value: string) => value === newPassword || "match",
            },
            { required: t.profile.confirmPasswordRequired, validate: t.profile.confirmPasswordMatch }
          )}
        </div>

        {/* Right-aligned submit */}
        <div className="flex justify-end">
          <button
            className="px-8 py-3 bg-[#113F67] text-white font-semibold rounded-lg transition-colors duration-200 hover:bg-[#0d2f4d] disabled:opacity-60 disabled:cursor-not-allowed"
            type="submit"
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <span className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin shrink-0"></span>
                {t.profile.changingPassword}
              </div>
            ) : (
              t.profile.changePasswordButton
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PasswordChangeForm;
